import type { ParsedRunStructure, RunPhase } from './runPlanParser';
import { getActivePhaseWithDistanceAnchors, zoneSpeechLabel } from './runPlanParser';
import {
  bucketHrZonesForElapsedWindow,
  type HrSample,
  type HrZone,
  type HrZoneId,
  type PhaseHrMetrics,
} from './heartRateZones';

export type CoachPhaseVerdict = 'met' | 'close' | 'missed' | 'partial' | 'n/a' | 'not_started';

export type CoachPhaseResult = {
  label: string;
  kind: string;
  planned: string;
  actual: string;
  verdict: CoachPhaseVerdict;
};

export type CoachGuidePerformanceReport = {
  planText: string;
  overallVerdict: 'met' | 'partial' | 'missed';
  overallSummary: string;
  phases: CoachPhaseResult[];
};

const VERDICT_RANK: Record<CoachPhaseVerdict, number> = {
  met: 4,
  close: 3,
  partial: 2,
  missed: 1,
  'n/a': 99,
  not_started: 0,
};

const TARGET_ZONE_MET_PCT = 65;
const TARGET_ZONE_CLOSE_PCT = 40;

function formatPaceSec(sec: number): string {
  if (!sec || !Number.isFinite(sec)) return '--:--';
  const totalSec = Math.max(0, Math.round(sec));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatDuration(sec: number): string {
  const totalSec = Math.max(0, Math.round(sec));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function describePlannedPhase(phase: RunPhase): string {
  const parts: string[] = [];
  if (phase.repInSet) {
    parts.push(`${phase.repInSet.rep} of ${phase.repInSet.totalReps}`);
  }
  if (phase.distanceKm) parts.push(`${phase.distanceKm} km`);
  if (phase.durationSeconds) parts.push(formatDuration(phase.durationSeconds));
  if (phase.paceSecondsPerKm) parts.push(`@ ${formatPaceSec(phase.paceSecondsPerKm)}/km`);
  if (phase.targetZone) parts.push(zoneSpeechLabel(phase.targetZone));
  return parts.length > 0 ? parts.join(' · ') : phase.label;
}

function isScoredWorkPhase(kind: string): boolean {
  return kind === 'interval' || kind === 'tempo' || kind === 'long' || kind === 'easy';
}

function hasMeasurableTarget(phase: RunPhase): boolean {
  return (phase.distanceKm ?? 0) > 0 || (phase.durationSeconds ?? 0) > 0;
}

/** Phases shown in the after-run plan vs actual block. */
function isReportablePhase(phase: RunPhase): boolean {
  if (phase.kind === 'warmup' || phase.kind === 'cooldown' || phase.kind === 'recovery') {
    return true;
  }
  if (isScoredWorkPhase(phase.kind)) return true;
  return hasMeasurableTarget(phase);
}

/** Phases that contribute to the overall met / missed verdict. */
function countsTowardScore(phase: RunPhase): boolean {
  if (phase.kind === 'recovery') return false;
  if (isScoredWorkPhase(phase.kind)) return true;
  return phase.kind === 'free' && hasMeasurableTarget(phase);
}

function worstVerdict(
  ...verdicts: Array<CoachPhaseVerdict | null | undefined>
): CoachPhaseVerdict {
  const valid = verdicts.filter(
    (v): v is CoachPhaseVerdict => !!v && v !== 'n/a' && v !== 'not_started',
  );
  if (valid.length === 0) return 'n/a';
  return valid.reduce((worst, v) => (VERDICT_RANK[v] < VERDICT_RANK[worst] ? v : worst));
}

function scoreDuration(
  phase: RunPhase,
  actualDurationSec: number,
  actualDistanceKm: number,
): CoachPhaseVerdict | null {
  if ((phase.distanceKm ?? 0) > 0 && actualDistanceKm > 0.02) {
    const distOk = actualDistanceKm >= (phase.distanceKm ?? 0) * 0.9;
    if (distOk) return 'met';
    if (actualDistanceKm >= (phase.distanceKm ?? 0) * 0.75) return 'close';
    return 'missed';
  }

  if ((phase.durationSeconds ?? 0) > 0 && actualDurationSec > 0) {
    const planned = phase.durationSeconds ?? 0;
    const delta = Math.abs(actualDurationSec - planned);
    if (delta <= planned * 0.15 + 12) return 'met';
    if (delta <= planned * 0.3 + 20) return 'close';
    return 'missed';
  }

  return null;
}

function scorePace(
  phase: RunPhase,
  actualDurationSec: number,
  actualDistanceKm: number,
): CoachPhaseVerdict | null {
  if (!phase.paceSecondsPerKm || actualDistanceKm <= 0.05 || actualDurationSec <= 0) return null;
  const actualPace = actualDurationSec / actualDistanceKm;
  const tolerance = phase.paceSecondsPerKm * 0.12 + 25;
  const delta = Math.abs(actualPace - phase.paceSecondsPerKm);
  if (delta <= tolerance) return 'met';
  if (delta <= tolerance * 1.8) return 'close';
  return 'missed';
}

function scoreHrZone(
  targetZone: HrZoneId | undefined,
  metrics: PhaseHrMetrics | null | undefined,
): CoachPhaseVerdict | null {
  if (!targetZone || !metrics) return null;
  if (metrics.targetZonePct >= TARGET_ZONE_MET_PCT) return 'met';
  if (metrics.targetZonePct >= TARGET_ZONE_CLOSE_PCT) return 'close';
  return 'missed';
}

function scorePhase(
  phase: RunPhase,
  actualDurationSec: number,
  actualDistanceKm: number,
  hrMetrics?: PhaseHrMetrics | null,
): CoachPhaseVerdict {
  if (phase.kind === 'recovery' || phase.kind === 'warmup' || phase.kind === 'cooldown') {
    return 'n/a';
  }

  const durationVerdict = scoreDuration(phase, actualDurationSec, actualDistanceKm);
  const paceVerdict = scorePace(phase, actualDurationSec, actualDistanceKm);
  const hrVerdict = scoreHrZone(phase.targetZone, hrMetrics);

  const verdicts = [durationVerdict, paceVerdict, hrVerdict].filter(Boolean);
  if (verdicts.length === 0) {
    return actualDurationSec > 0 || actualDistanceKm > 0.02 ? 'partial' : 'missed';
  }

  return worstVerdict(...verdicts);
}

function formatHrDetail(phase: RunPhase, metrics: PhaseHrMetrics): string | null {
  if (!phase.targetZone) return null;
  const target = phase.targetZone.toUpperCase();
  const pct = metrics.targetZonePct;
  if (pct >= TARGET_ZONE_MET_PCT) return `${pct}% in ${target}`;
  if (metrics.dominantZone && metrics.dominantZone !== phase.targetZone) {
    return `${pct}% in ${target} (mostly ${metrics.dominantZone.toUpperCase()})`;
  }
  return `${pct}% in ${target}`;
}

function formatActualLine(
  phase: RunPhase,
  actualDurationSec: number,
  actualDistanceKm: number,
  verdict: CoachPhaseVerdict,
  status: 'completed' | 'partial' | 'not_started',
  hrMetrics?: PhaseHrMetrics | null,
): string {
  if (status === 'not_started') return 'Not started';

  const parts: string[] = [];
  if (actualDistanceKm > 0.02) {
    parts.push(`${actualDistanceKm.toFixed(2)} km`);
  }
  if (actualDistanceKm > 0.05 && actualDurationSec > 0) {
    parts.push(`avg ${formatPaceSec(actualDurationSec / actualDistanceKm)}/km`);
  } else if (actualDurationSec > 0) {
    parts.push(formatDuration(actualDurationSec));
  }

  if (hrMetrics && phase.targetZone) {
    const hrDetail = formatHrDetail(phase, hrMetrics);
    if (hrDetail) parts.push(hrDetail);
  } else if (hrMetrics?.avgBpm) {
    parts.push(`avg ${hrMetrics.avgBpm} bpm`);
  }

  if (parts.length === 0) {
    return status === 'partial' ? 'In progress — no measurable data' : '—';
  }

  const body = parts.join(' · ');
  if (phase.kind === 'recovery' || phase.kind === 'warmup' || phase.kind === 'cooldown') {
    return body;
  }
  if (verdict === 'met') return `${body} — target met`;
  if (verdict === 'close') return `${body} — close to target`;
  if (status === 'partial') return `${body} — incomplete`;
  return `${body} — target not reached`;
}

export function buildCoachGuidePerformanceReport(args: {
  planText: string;
  structure: ParsedRunStructure;
  elapsedSeconds: number;
  distanceKm: number;
  phaseEndElapsedByIndex: Map<number, number>;
  phaseStartDistanceKmByIndex: Map<number, number>;
  phaseEndDistanceKmByIndex?: Map<number, number>;
  hrSamples?: HrSample[];
  hrZones?: HrZone[];
  runStartMs?: number;
}): CoachGuidePerformanceReport | null {
  const {
    planText,
    structure,
    elapsedSeconds,
    distanceKm,
    phaseEndElapsedByIndex,
    phaseStartDistanceKmByIndex,
    phaseEndDistanceKmByIndex,
    hrSamples,
    hrZones,
    runStartMs,
  } = args;
  if (!planText.trim() || !structure.hasStructure || structure.phases.length === 0) return null;

  const lastPhaseIndexRef = { current: -1 };
  const active = getActivePhaseWithDistanceAnchors(
    structure,
    elapsedSeconds,
    distanceKm,
    phaseStartDistanceKmByIndex,
    phaseEndElapsedByIndex,
    lastPhaseIndexRef,
  );
  const activeIndex = active?.phase.index ?? structure.phases.length;
  const canScoreHr = !!(hrSamples?.length && hrZones?.length && runStartMs);

  const phases: CoachPhaseResult[] = [];
  let prevElapsed = 0;
  let metCount = 0;
  let scoredCount = 0;
  let missedWorkCount = 0;
  let usedHrScoring = false;

  for (const phase of structure.phases) {
    if (!isReportablePhase(phase)) {
      continue;
    }

    const endElapsed = phaseEndElapsedByIndex.get(phase.index);
    const started =
      phaseStartDistanceKmByIndex.has(phase.index) ||
      endElapsed != null ||
      phase.index <= activeIndex;

    if (!started) {
      phases.push({
        label: phase.label,
        kind: phase.kind,
        planned: describePlannedPhase(phase),
        actual: 'Not started',
        verdict: 'not_started',
      });
      if (countsTowardScore(phase)) {
        missedWorkCount += 1;
      }
      continue;
    }

    let actualDuration = 0;
    let actualDistanceKm = 0;
    let status: 'completed' | 'partial' | 'not_started' = 'partial';
    let phaseStartElapsed = prevElapsed;

    if (endElapsed != null) {
      actualDuration = Math.max(0, endElapsed - prevElapsed);
      phaseStartElapsed = prevElapsed;
      prevElapsed = endElapsed;
      status = 'completed';
    } else if (phase.index === activeIndex) {
      actualDuration = Math.max(0, elapsedSeconds - prevElapsed);
      phaseStartElapsed = prevElapsed;
      status = 'partial';
    }

    if ((phase.distanceKm ?? 0) > 0) {
      const plannedKm = phase.distanceKm ?? 0;
      let startDist = phaseStartDistanceKmByIndex.get(phase.index);
      if (startDist == null || startDist < 0) {
        for (let i = phase.index - 1; i >= 0; i--) {
          const prevEnd = phaseEndDistanceKmByIndex?.get(i);
          if (prevEnd != null && prevEnd >= 0) {
            startDist = prevEnd;
            break;
          }
        }
      }
      if (startDist == null && phase.index === 0) startDist = 0;

      let endDist = phaseEndDistanceKmByIndex?.get(phase.index);
      if (endDist == null) {
        // Prefer the next phase start of ANY kind (cool-down is often time-only).
        for (const next of structure.phases) {
          if (next.index <= phase.index) continue;
          const nextStart = phaseStartDistanceKmByIndex.get(next.index);
          if (nextStart != null && nextStart >= 0) {
            endDist = nextStart;
            break;
          }
        }
      }
      if (endDist == null && status === 'partial') {
        endDist = distanceKm;
      }
      if (endDist == null && status === 'completed' && startDist != null) {
        const hasLaterPhase = structure.phases.some((p) => p.index > phase.index);
        // Never attribute post-block cool-down / extra km to a finished distance block.
        endDist = hasLaterPhase
          ? startDist + plannedKm
          : distanceKm;
      }

      if (startDist != null && endDist != null) {
        actualDistanceKm = Math.max(0, endDist - startDist);
      } else if (startDist != null && status === 'partial') {
        actualDistanceKm = Math.max(0, distanceKm - startDist);
      }
    }

    const phaseEndElapsed =
      endElapsed ?? (status === 'partial' ? elapsedSeconds : phaseStartElapsed + actualDuration);
    const hrMetrics =
      canScoreHr && phase.targetZone && phaseEndElapsed > phaseStartElapsed
        ? bucketHrZonesForElapsedWindow(
            hrSamples!,
            hrZones!,
            runStartMs!,
            phaseStartElapsed,
            phaseEndElapsed,
            phase.targetZone,
          )
        : null;
    if (hrMetrics && phase.targetZone && countsTowardScore(phase)) {
      usedHrScoring = true;
    }

    const verdict = scorePhase(phase, actualDuration, actualDistanceKm, hrMetrics);
    if (countsTowardScore(phase)) {
      if (verdict === 'met' || verdict === 'close' || verdict === 'missed' || verdict === 'partial') {
        scoredCount += 1;
        if (verdict === 'met') metCount += 1;
        if (verdict === 'missed' || verdict === 'partial') missedWorkCount += 1;
      }
    }

    phases.push({
      label: phase.label,
      kind: phase.kind,
      planned: describePlannedPhase(phase),
      actual: formatActualLine(
        phase,
        actualDuration,
        actualDistanceKm,
        verdict,
        status,
        hrMetrics,
      ),
      verdict,
    });
  }

  if (phases.length === 0) return null;

  let overallVerdict: CoachGuidePerformanceReport['overallVerdict'] = 'missed';
  if (scoredCount > 0 && metCount === scoredCount) {
    overallVerdict = 'met';
  } else if (scoredCount > 0 && metCount > 0) {
    overallVerdict = metCount / scoredCount >= 0.5 ? 'partial' : 'missed';
  } else if (metCount === 0) {
    overallVerdict = 'missed';
  }

  const overallSummary =
    overallVerdict === 'met'
      ? usedHrScoring
        ? 'You hit the planned duration, pace, and heart-rate targets — strong execution.'
        : 'You hit the planned targets — strong execution.'
      : overallVerdict === 'partial'
        ? usedHrScoring
          ? 'Mixed execution — some blocks matched the plan, but pace or heart rate drifted in others.'
          : 'Mixed execution — some blocks on target, others need work.'
        : missedWorkCount > 0 && metCount === 0
          ? usedHrScoring
            ? 'Targets were missed — review pacing and staying in the planned heart-rate zones.'
            : 'You did not hit the planned targets — review pacing and recovery.'
          : usedHrScoring
            ? 'Several blocks missed the plan — review pacing and heart-rate control.'
            : 'Several blocks missed the plan — review pacing and recovery.';

  return {
    planText: planText.trim(),
    overallVerdict,
    overallSummary,
    phases,
  };
}

export function formatCoachGuideReportForArnold(report: CoachGuidePerformanceReport): string {
  const lines = [
    `PLANNED WORKOUT: ${report.planText}`,
    `OVERALL: ${report.overallVerdict.toUpperCase()} — ${report.overallSummary}`,
    'BLOCK-BY-BLOCK (planned vs actual):',
    ...report.phases.map((p) => `  ${p.label}: planned ${p.planned} → actual ${p.actual}`),
  ];
  return lines.join('\n');
}
